import * as Comlink from 'comlink';
import { readPreference, writePreference } from '@/lib/preferences';
import { keepStorage } from '@/lib/model-cache';
import { toEngineError } from './errors';
import type { EngineApi } from './worker/engine.worker';
import type { PrepareRecordingOptions } from './media/prepare-recording';
import type { CaptionWord } from '@/core/captions/types';
import type {
  ExportJob,
  ExportResult,
  PrivacyScanOptions,
  ProgressCallback,
  RegionTrackRequest,
  TelemetryResult,
  TranscribeOptions,
  TranscriptionPart,
  TranscriptionResult,
} from './types';

export class TaskCancelledError extends Error {
  constructor() {
    super('Task cancelled');
    this.name = 'TaskCancelledError';
  }
}

export interface Task<T> {
  readonly promise: Promise<T>;
  /** Stops the task immediately by terminating its worker. */
  cancel(): void;
}

/**
 * Runs one job in a dedicated worker. Terminating the worker is the only
 * cancellation that is instant and guaranteed to release decoders, encoders and
 * GPU memory, so every task gets its own short-lived worker.
 */
function runTask<T>(job: (api: Comlink.Remote<EngineApi>) => Promise<T>): Task<T> {
  const worker = new Worker(new URL('./worker/engine.worker.ts', import.meta.url), { type: 'module', name: 'shycut-engine' });
  const api = Comlink.wrap<EngineApi>(worker);
  let settled = false;
  let rejectCancel: (reason: unknown) => void = () => undefined;
  const cancelled = new Promise<never>((_, reject) => (rejectCancel = reject));
  const finish = () => {
    settled = true;
    api[Comlink.releaseProxy]();
    worker.terminate();
  };
  const workerFailure = new Promise<never>((_, reject) => {
    worker.addEventListener('error', (event) => reject(new Error(event.message || 'Worker crashed')));
  });
  const promise = Promise.race([job(api), cancelled, workerFailure])
    .catch((error: unknown) => {
      throw error instanceof TaskCancelledError ? error : toEngineError(error);
    })
    .finally(finish);
  return {
    promise,
    cancel() {
      if (settled) return;
      rejectCancel(new TaskCancelledError());
    },
  };
}

const progressProxy = (onProgress?: ProgressCallback) => (onProgress ? Comlink.proxy(onProgress) : undefined);

/** Remembers, per device, that the GPU failed under Whisper. */
export interface GpuMemory {
  readonly allowed: () => boolean;
  readonly failed: () => void;
}

const captionGpu: GpuMemory = {
  allowed: () => readPreference('caption-gpu-failed') === null,
  failed: () => writePreference('caption-gpu-failed', new Date().toISOString()),
};

/**
 * Speech to text that survives a GPU failing part-way (a phone losing its GPU
 * under memory pressure or in the background): what was written down is kept,
 * and a fresh worker carries on on the CPU from the piece that failed. A device
 * whose GPU failed while working starts on the CPU next time.
 */
export function resumableTranscription(
  options: TranscribeOptions,
  start: (options: TranscribeOptions) => Task<TranscriptionPart>,
  gpu: GpuMemory = captionGpu,
): Task<TranscriptionResult> {
  let current: Task<TranscriptionPart> | null = null;
  let cancelled = false;
  const promise = (async (): Promise<TranscriptionResult> => {
    const words: CaptionWord[] = [];
    let next: TranscribeOptions = { ...options, gpu: gpu.allowed() };
    for (;;) {
      if (cancelled) throw new TaskCancelledError();
      current = start(next);
      const part = await current.promise;
      words.push(...part.words);
      if (part.status === 'done') return { words, language: part.language };
      // A CPU run never reports a GPU failure; if one did, carrying on would loop forever.
      if (next.gpu === false) throw new Error('Transcription reported a GPU failure without a GPU');
      // Only a failure while working says something about this device; a failed load may be the network.
      if (part.stage === 'run') gpu.failed();
      next = { ...options, gpu: false, resume: { from: part.stoppedAt, language: part.language } };
    }
  })();
  return {
    promise,
    cancel() {
      cancelled = true;
      current?.cancel();
    },
  };
}

/** Remembers, per device, video encodings that took frames and never output any. */
export interface EncoderMemory {
  readonly broken: () => readonly string[];
  readonly remember: (encodings: readonly string[]) => void;
}

const exportEncoders: EncoderMemory = {
  broken() {
    try {
      const stored: unknown = JSON.parse(readPreference('export-broken-encoders') ?? '[]');
      return Array.isArray(stored) ? stored.filter((entry): entry is string => typeof entry === 'string') : [];
    } catch {
      return [];
    }
  },
  remember(encodings) {
    if (encodings.length === 0) return;
    writePreference('export-broken-encoders', JSON.stringify([...new Set([...this.broken(), ...encodings])]));
  },
};

/**
 * An export that skips encodings this device broke on before, and learns new
 * ones: a hung encoder costs a trial of several seconds, so it is paid once.
 */
export function rememberingExport(job: ExportJob, start: (job: ExportJob) => Task<ExportResult>, memory: EncoderMemory = exportEncoders): Task<ExportResult> {
  const task = start({ ...job, skipEncodings: memory.broken() });
  return {
    promise: task.promise.then((result) => {
      memory.remember(result.brokenEncodings);
      return result;
    }),
    cancel: task.cancel,
  };
}

/** Main-thread facade over the media engine. */
export const engine = {
  probe: (file: File) => runTask((api) => api.probe(file)),
  /** What a file says about where, when and on what it was made; reads tags only, no frames. */
  inspect: (file: File) => runTask((api) => api.inspect(file)),
  /** Length and format of a sound file (music, a voice-over). */
  probeSound: (file: File) => runTask((api) => api.probeSound(file)),
  analyzeAudio: (file: File, onProgress?: ProgressCallback) => runTask((api) => api.analyzeAudio(file, progressProxy(onProgress))),
  filmstrip: (file: File, count: number, height: number) => runTask((api) => api.filmstrip(file, count, height)),
  scanPrivacy: (file: File, options: PrivacyScanOptions, onProgress?: ProgressCallback) => {
    // Models are about to be downloaded: ask the browser to keep them.
    void keepStorage();
    return runTask((api) => api.scanPrivacy(file, options, progressProxy(onProgress)));
  },
  trackRegion: (file: File, request: RegionTrackRequest, onProgress?: ProgressCallback) =>
    runTask((api) => api.trackRegion(file, request, progressProxy(onProgress))),
  transcribe: (file: File, options: TranscribeOptions, onProgress?: ProgressCallback) => {
    void keepStorage();
    return resumableTranscription(options, (next) =>
      // Comlink's remote type distributes over the union, so the result type is restated.
      runTask((api) => api.transcribe(file, next, progressProxy(onProgress)) as Promise<TranscriptionPart>),
    );
  },
  /** Where a screen recording changes over time (for auto zoom), and the notifications in it. */
  analyzeScreen: (file: File, onProgress?: ProgressCallback) => runTask((api) => api.analyzeScreen(file, progressProxy(onProgress))),
  /** Splits a raw screen recording into the screen (with sound) and the camera, losslessly. */
  prepareRecording: (raw: Blob, options: PrepareRecordingOptions, onProgress?: ProgressCallback) =>
    runTask((api) => api.prepareRecording(raw, options, progressProxy(onProgress))),
  joinChapters: (files: File[], outputName: string, onProgress?: ProgressCallback) =>
    runTask((api) => api.joinChapters(files, outputName, progressProxy(onProgress))),
  /** GPS data for the gauge; pass the original chapters for a joined video. */
  // Comlink's remote type distributes over the union, so the result type is restated.
  readTelemetry: (files: File[]) => runTask((api) => api.readTelemetry(files) as Promise<TelemetryResult>),
  exportVideo: (job: ExportJob, onProgress?: ProgressCallback) => rememberingExport(job, (next) => runTask((api) => api.exportVideo(next, progressProxy(onProgress)))),
};

export const isCancelled = (error: unknown): error is TaskCancelledError => error instanceof TaskCancelledError;
